import React, { useState } from "react";
import Select from "react-select";
import { DateTime } from "luxon";

import { DateRangePicker } from "../../../../../../mogul/components/DashboardGraph";
import useAvailableFullCircleAfterCareOptions from "../../../../../../mogul/hooks/useAvailableFullCircleAfterCareOptions";

export interface FullCircleFilterObject {
    selectedOrgIds: string[];
    selectedPreneedOptions: string[];
    onlyUnSyncedWith360: boolean;
    includeLeadsRegardlessOfPreneedStatus: boolean;
    startingUnixTimestamp?: number;
    endingUnixTimestamp?: number;
}

export default function FullCircleFilterList({
    onCancel,
    onReset,
    currentFilterObject,
    onApplyFilter,
    isFilterChild = false,
}: {
    onCancel: () => void;
    onReset: () => void;
    currentFilterObject: FullCircleFilterObject | null;
    onApplyFilter: (filterObject: FullCircleFilterObject) => void;
    isFilterChild?: boolean;
}) {
    const {
        availableOrgSelections,
        availablePreneedLeadInterestOptions,
    } = useAvailableFullCircleAfterCareOptions();

    const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>(
        currentFilterObject ? currentFilterObject.selectedOrgIds : []
    );
    const [selectedPreneedOptions, setSelectedPreneedOptions] = useState<string[]>(
        currentFilterObject ? currentFilterObject.selectedPreneedOptions : []
    );
    const [onlyUnSyncedWith360, setOnlyUnSyncedWith360] = useState<boolean>(
        currentFilterObject ? currentFilterObject.onlyUnSyncedWith360 : false
    );
    const [
        includeLeadsRegardlessOfPreneedStatus,
        setIncludeLeadsRegardlessOfPreneedStatus,
    ] = useState<boolean>(
        currentFilterObject
            ? currentFilterObject.includeLeadsRegardlessOfPreneedStatus
            : false
    );

    const [startingUnixTimestamp, setStartingUnixTimestamp] = useState<number>(
        currentFilterObject?.startingUnixTimestamp ||
        Math.floor(
            DateTime.local()
                .setZone("America/Chicago")
                .minus({ days: 365 })
                .startOf("day")
                .toSeconds()
        )
    );
    const [endingUnixTimestamp, setEndingUnixTimestamp] = useState<number>(
        currentFilterObject?.endingUnixTimestamp ||
        Math.floor(
            DateTime.local().setZone("America/Chicago").toSeconds()
        )
    );

    return (
        <div>
            {!isFilterChild && (
                <>
                    <div className="px-7 py-5">
                        <div className="fs-5 text-dark fw-bolder">Filter List</div>
                    </div>
                    <div className="separator border-gray-200"></div>
                </>
            )}

            <div className={isFilterChild ? "" : "px-7 py-5"}>
                {/* Funeral Home Selector */}
                <div className="mb-5">
                    <label className="form-label fw-bold">Funeral Home</label>
                    <div>
                        <Select
                            options={[
                                { value: "", label: "-All Funeral Homes-" },
                                ...availableOrgSelections.map((option) => ({
                                    value: `${option.org_id}`,
                                    label: option.org_name,
                                })),
                            ]}
                            onChange={(selectedOptions) => {
                                setSelectedOrgIds(
                                    selectedOptions ? selectedOptions.map((x) => x.value) : []
                                );
                            }}
                            isMulti
                            filterOption={(option, searchValue) => {
                                const words = searchValue.toLowerCase().split(" ");
                                return words.every((word) =>
                                    option.label.toLowerCase().includes(word)
                                );
                            }}
                            value={availableOrgSelections
                                .filter((x) => selectedOrgIds.includes(`${x.org_id}`))
                                .map((option) => ({
                                    value: `${option.org_id}`,
                                    label: option.org_name,
                                }))}
                            isDisabled={isFilterChild}
                        />
                    </div>
                </div>

                {/* Pre-Need Interest Selector */}
                <div className="mb-5">
                    <label className="form-label fw-bold">Interested in Pre-Need?</label>
                    <div>
                        <Select
                            options={[
                                { value: "", label: "-All Options-" },
                                ...availablePreneedLeadInterestOptions.map((option) => ({
                                    value: `${option.id}`,
                                    label: option.label,
                                })),
                            ]}
                            onChange={(selectedOptions) => {
                                setSelectedPreneedOptions(
                                    selectedOptions ? selectedOptions.map((x) => x.value) : []
                                );
                            }}
                            value={availablePreneedLeadInterestOptions
                                .filter((x) => selectedPreneedOptions.includes(`${x.id}`))
                                .map((option) => ({
                                    value: `${option.id}`,
                                    label: option.label,
                                }))}
                            isMulti
                            filterOption={(option, searchValue) => {
                                const words = searchValue.toLowerCase().split(" ");
                                return words.every((word) =>
                                    option.label.toLowerCase().includes(word)
                                );
                            }}
                            isDisabled={isFilterChild}
                        />
                    </div>
                </div>

                {/* Only Un-Synced With 360 Toggle */}
                {!isFilterChild && (
                    <div className="mb-5">
                        <label className="form-label fw-bold">
                            Only those un-synced with 360:
                        </label>
                        <div className="form-check form-switch form-switch-sm form-check-custom form-check-solid">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                onChange={(e) => setOnlyUnSyncedWith360(e.target.checked)}
                                checked={onlyUnSyncedWith360}
                            />
                            <label className="form-check-label">Enabled</label>
                        </div>
                    </div>
                )}

                {/* Include Leads Regardless Of Pre-Need Status Toggle */}

                {!isFilterChild && (
                    <div className="mb-5">
                        <label className="form-label fw-bold">
                            Include leads regardless of pre-need status:
                        </label>
                        <div className="form-check form-switch form-switch-sm form-check-custom form-check-solid">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                onChange={(e) =>
                                    setIncludeLeadsRegardlessOfPreneedStatus(e.target.checked)
                                }
                                checked={includeLeadsRegardlessOfPreneedStatus}
                            />
                            <label className="form-check-label">Enabled</label>
                        </div>
                    </div>
                )}

                {/* Date Range Picker */}
                <div className="mb-5">
                    <label className="form-label fw-bold">Date Range</label>
                    <div style={{ maxWidth: 420 }}>
                        <DateRangePicker
                            startingUnixTimestamp={startingUnixTimestamp}
                            setStartingUnixTimestamp={setStartingUnixTimestamp}
                            endingUnixTimestamp={endingUnixTimestamp}
                            setEndingUnixTimestamp={setEndingUnixTimestamp}
                            hideUpdateButton
                            disabled={!!isFilterChild}
                        />
                    </div>
                </div>

                {/* Note for Filter Child */}
                {isFilterChild && (
                    <div className="mb-5">
                        <div
                            className="alert alert-info py-2 px-3 mb-0"
                            style={{ fontSize: 14 }}
                        >
                            <strong>Note:</strong> Filtering must be changed before
                            exporting, to ensure the correct leads are exported.
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                {!isFilterChild && (
                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-sm btn-light btn-active-light-primary me-2"
                            onClick={() => onReset()}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light btn-active-light-primary me-2"
                            onClick={() => onCancel()}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() =>
                                onApplyFilter({
                                    selectedOrgIds,
                                    selectedPreneedOptions,
                                    onlyUnSyncedWith360,
                                    includeLeadsRegardlessOfPreneedStatus,
                                    startingUnixTimestamp,
                                    endingUnixTimestamp,
                                })
                            }
                        >
                            Apply
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
